import { ChevronDown, LogOut, Settings, User } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';

/**
 * Top-bar user menu (05 §1.3). Hand-written dropdown: click or Escape closes,
 * a click outside closes, and the trigger keeps `aria-expanded`.
 *
 * "Sign out" calls `POST /auth/staff/logout`, which revokes the refresh token
 * and its whole rotation chain (05 §2.13) — so every tab of this session dies,
 * not just this one. The local session is cleared even when that call fails.
 */
export function UserMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const actor = useAuthStore((state) => state.actor);
  const logout = useAuthStore((state) => state.logout);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  async function onSignOut() {
    setPending(true);
    try {
      await logout();
      toast.success(t('auth.signedOut'));
      navigate('/login', { replace: true });
    } finally {
      setPending(false);
      setOpen(false);
    }
  }

  const label = actor?.name ?? t('user.guest');

  return (
    <div ref={containerRef} className="relative">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('user.menu')}
        data-testid="user-menu"
        className="gap-2"
      >
        <User aria-hidden="true" />
        <span className="hidden max-w-32 truncate sm:inline" data-testid="user-menu-name">
          {label}
        </span>
        {actor ? (
          <Badge variant={actor.role === 'ADMIN' ? 'default' : 'secondary'} data-testid="user-role">
            {t(`user.role.${actor.role}`)}
          </Badge>
        ) : null}
        <ChevronDown
          aria-hidden="true"
          className={cn('transition-transform', open && 'rotate-180')}
        />
      </Button>

      {open ? (
        <div
          role="menu"
          aria-label={t('user.menu')}
          className="absolute end-0 z-50 mt-1 w-64 rounded-md border bg-popover p-1 text-popover-foreground shadow-lg animate-fade-in"
        >
          <div className="px-3 py-2">
            <p className="truncate text-sm font-medium">{label}</p>
            <p className="truncate text-xs text-muted-foreground" dir="ltr">
              {actor?.email ?? '—'}
            </p>
            {actor ? (
              <p className="mt-1 text-xs text-muted-foreground">{t(`user.role.${actor.role}`)}</p>
            ) : null}
          </div>
          <div className="my-1 h-px bg-border" />
          <Link
            to="/settings"
            role="menuitem"
            data-testid="user-menu-settings"
            onClick={() => setOpen(false)}
            className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground"
          >
            <Settings aria-hidden="true" className="size-4" />
            {t('nav.settings')}
          </Link>
          <button
            type="button"
            role="menuitem"
            data-testid="logout-button"
            disabled={pending}
            onClick={() => {
              void onSignOut();
            }}
            className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-sm text-destructive hover:bg-destructive/10 disabled:opacity-60"
          >
            {pending ? (
              <Spinner className="size-4" />
            ) : (
              <LogOut aria-hidden="true" className="size-4 icon-directional" />
            )}
            {t('user.signOut')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
